<template>
  <nav class="top-bar-navigation" ref="navigationContainer">
    <div class="top-bar-navigation__visible-items" ref="visibleItemsContainer">
      <VcNavItem
        v-for="item in visibleItems"
        :key="item.uid"
        :mode="navItemMode"
        :label="getItemTitle(item)"
        :is-selected="item.uid === activeItemUid"
        :is-external="isItemExternal(item)"
        :data-qa="`nav-item-${item.key}`"
        @click="handleItemClick(item)"
      >
        <template v-if="pulseBadgeCount && isPulseMenuItem(item)" #default>
          {{ getItemTitle(item) }}
          <VcBadge inline
                   flavor="neutral"
                   data-qa="nav-item-pulse-counter"
                   :badgeText="pulseBadgeCount" />
        </template>
      </VcNavItem>
    </div>

    <VcMenu
      v-if="overflowItems.length > 0"
      v-model="isMoreMenuOpen"
      class="top-bar-navigation__more-menu"
      offset-y
      left
    >
      <template #activator="{ on, attrs }">
        <VcNavItem
          ref="moreButton"
          :label="$t('common.navigation.more')"
          :is-selected="hasActiveInOverflow"
          :mode="navItemMode"
          icon="icon-chevron-down"
          :icon-size="16"
          data-qa="nav-item-more"
          v-bind="attrs"
          v-on="on"
        />
      </template>
      <VcList
        :items="overflowListItems"
        data-qa="nav-overflow-list"
        @select="handleOverflowItemSelect"
      />
    </VcMenu>
  </nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import justDebounce from 'just-debounce';
import { isPulseEnabled, isPulseMenuItem } from '@/modules/pulse/utils/pulseNavigation';
import { isExternalUrl } from '../utils/navigationUtils';
import { isActionExternal } from '../utils/actionExecutor';

const RESIZE_DEBOUNCE_MS = 150;

export default {
  name: 'TopBarNavigation',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    activeItemUid: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      visibleCount: 0,
      resizeObserver: null,
      itemWidths: [],
      moreButtonWidth: 80,
      isMoreMenuOpen: false,
    };
  },
  computed: {
    ...mapGetters('PlatformNavigationStore', ['getItemTitle']),
    ...mapGetters('SearchBarStore', { hiddenBySearch: 'showSearchResults' }),
    ...mapGetters('BusinessStore', ['isTextColorWhite', 'hasFeature']),
    ...mapGetters('PulseStore', { pulseNeedsAttentionCount: 'needsAttentionCount' }),
    pulseBadgeCount() {
      return isPulseEnabled(this.hasFeature) ? this.pulseNeedsAttentionCount : 0;
    },
    navItemMode() {
      return this.isTextColorWhite ? 'dark' : 'light';
    },
    visibleItems() {
      if (this.visibleCount === 0 || this.visibleCount >= this.items.length) {
        return this.items;
      }
      return this.items.slice(0, this.visibleCount);
    },
    overflowItems() {
      if (this.visibleCount === 0 || this.visibleCount >= this.items.length) {
        return [];
      }
      return this.items.slice(this.visibleCount);
    },
    hasActiveInOverflow() {
      return this.overflowItems.some((item) => item.uid === this.activeItemUid);
    },
    /**
     * Transform overflow items into VcList items format
     * VcList expects items with identifier (number) and label properties
     */
    overflowListItems() {
      return this.overflowItems.map((item, index) => ({
        identifier: index,
        label: this.getItemTitle(item),
        selected: item.uid === this.activeItemUid,
        // Store original item reference for click handler
        originalItem: item,
      }));
    },
  },
  created() {
    this.debouncedOnResize = justDebounce(() => {
      requestAnimationFrame(() => {
        this.calculateVisibleItems();
      });
    }, RESIZE_DEBOUNCE_MS);
  },
  mounted() {
    this.initResizeObserver();
    // Defer initial calculation until after flex layout has run (nav gets its real width)
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.calculateVisibleItems();
      });
    });
    // Listen for overlay click to close the menu
    this.$root.$on('overlay-clicked', this.onOverlayClicked);
  },
  beforeDestroy() {
    this.destroyResizeObserver();
    this.$root.$off('overlay-clicked', this.onOverlayClicked);
  },
  watch: {
    items: {
      handler() {
        this.remeasureItems();
      },
      deep: true,
    },
    pulseBadgeCount() {
      this.remeasureItems();
    },
    isMoreMenuOpen(isOpen) {
      // Only show overlay when opening - don't hide on close to avoid
      // race condition with other components that may be taking over the overlay
      if (isOpen) {
        this.setIsOverlayDisplay(true);
      }
    },
  },
  methods: {
    ...mapActions('AngularStore', ['setIsOverlayDisplay']),
    isPulseMenuItem(item) {
      return isPulseMenuItem(item, this.hasFeature);
    },
    remeasureItems() {
      this.itemWidths = [];
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.calculateVisibleItems();
        });
      });
    },
    onOverlayClicked() {
      if (this.isMoreMenuOpen) {
        this.isMoreMenuOpen = false;
        this.setIsOverlayDisplay(false);
      }
    },
    initResizeObserver() {
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.debouncedOnResize);
        return;
      }

      this.resizeObserver = new ResizeObserver(this.debouncedOnResize);

      if (this.$refs.navigationContainer) {
        this.resizeObserver.observe(this.$refs.navigationContainer);
      }
    },
    destroyResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else {
        window.removeEventListener('resize', this.debouncedOnResize);
      }
    },
    calculateVisibleItems() {
      if (this.hiddenBySearch) return;
      if (!this.$refs.navigationContainer || this.items.length === 0) {
        this.visibleCount = this.items.length;
        return;
      }

      const container = this.$refs.navigationContainer;
      const containerWidth = container.offsetWidth;

      // Need to measure item widths: render all items first, then measure
      if (this.itemWidths.length !== this.items.length) {
        this.visibleCount = this.items.length;
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.measureAndCalculate();
          });
        });
        return;
      }

      this.applyVisibleCount(containerWidth);
    },
    measureAndCalculate() {
      const container = this.$refs.navigationContainer;
      const visibleContainer = this.$refs.visibleItemsContainer;
      if (!container || !visibleContainer) return;

      const containerWidth = container.offsetWidth;

      // Measure each nav item (direct children of visible items container)
      const itemElements = Array.from(visibleContainer.children).filter(
        (el) => el && typeof el.offsetWidth === 'number',
      );
      if (itemElements.length !== this.items.length) return;

      this.itemWidths = itemElements.map((el) => el.offsetWidth);
      // Measure more button when it's in DOM (after we've had overflow at least once)
      if (this.$refs.moreButton && this.$refs.moreButton.offsetWidth) {
        this.moreButtonWidth = this.$refs.moreButton.offsetWidth;
      }

      this.applyVisibleCount(containerWidth);
    },
    applyVisibleCount(containerWidth) {
      const gap = 4; // css gap 4px;
      const availableWidth = containerWidth - this.moreButtonWidth - gap;

      if (this.itemWidths.length === 0) {
        this.visibleCount = this.items.length;
        return;
      }

      // Find max N such that (sum of first N item widths) + N*gap <= availableWidth
      let total = 0;
      let count = 0;
      for (let i = 0; i < this.itemWidths.length; i++) {
        const nextTotal = total + this.itemWidths[i];
        if (nextTotal > availableWidth) break;
        total = nextTotal;
        count = i + 1;
      }

      count = Math.max(1, Math.min(count, this.items.length));
      this.visibleCount = count;

      // Update more button width when it becomes visible for next resize
      if (count < this.items.length) {
        this.$nextTick(() => {
          if (this.$refs.moreButton && this.$refs.moreButton.offsetWidth) {
            this.moreButtonWidth = this.$refs.moreButton.offsetWidth;
          }
        });
      }
    },
    handleItemClick(item) {
      this.$emit('item-click', item);
    },
    handleOverflowItemSelect(identifier) {
      const listItem = this.overflowListItems.find((item) => item.identifier === identifier);
      if (listItem && listItem.originalItem) {
        this.isMoreMenuOpen = false;
        this.setIsOverlayDisplay(false);
        this.handleItemClick(listItem.originalItem);
      }
    },
    isItemExternal(item) {
      if (item.action) return isActionExternal(item);
      return isExternalUrl(item.url);
    },
  },
};
</script>

<style lang="scss" scoped>
.top-bar-navigation {
  display: flex;
  align-items: center;
  gap: var(--size-value1);
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;

  &-item {
    &:hover, &:focus {
      background-color: var(--neutral-lighten-2);
      color: var(--text-color-primary);
    }
  }

  &__visible-items {
    display: flex;
    align-items: center;
    gap: var(--size-value1);
    flex-shrink: 0;
  }

  &__more-menu {
    flex-shrink: 0;
  }
}
</style>
